import { useBreakpointValue } from "@chakra-ui/react";
import { useCallback, useEffect, useRef, useState } from "react";

import { useCompareEffect, useLastTruthyValue } from "@nens/react/hooks";
import { Schematisation } from "@nens/threedi/openapi-client";

import { BASE_PAGE_OVERFLOW_ID } from "@/components/base-page";

type UseSchematisationListProps = {
  isSectionExpanded?: boolean;
  schematisations: Schematisation[];
};

export function useSchematisationList({
  isSectionExpanded = true,
  schematisations,
}: UseSchematisationListProps) {
  // The overview hook already did the filtering; this hook only paginates and shapes the grid state.
  const count = schematisations.length;
  const lastKnownCount = useLastTruthyValue(count) ?? 0;

  const itemsInFirstRow = useBreakpointValue({ base: 1, sm: 2, md: 3, lg: 4 }) ?? 1;
  const itemsInCrement = itemsInFirstRow * 3;
  const [amountShown, setAmountShown] = useState(itemsInCrement);
  if (amountShown < itemsInCrement) {
    setAmountShown(itemsInCrement);
  }
  useCompareEffect(() => {
    setAmountShown(itemsInCrement);
  }, [schematisations.length]);

  const showMore = useCallback(
    () => setAmountShown((prev) => prev + itemsInCrement),
    [itemsInCrement]
  );

  const listEndRef = useRef<HTMLDivElement | null>(null);

  const remainingSchematisations = Math.max(0, lastKnownCount - amountShown);
  const trackShowMore = isSectionExpanded && remainingSchematisations > 0;

  const displaySchematisationsAmount = isSectionExpanded ? amountShown : itemsInFirstRow;

  useEffect(() => {
    if (!trackShowMore || !listEndRef.current) return;

    const overflowElement = document.getElementById(BASE_PAGE_OVERFLOW_ID);
    if (!overflowElement) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) showMore();
      },
      {
        root: overflowElement,
        threshold: 1,
      }
    );

    observer.observe(listEndRef.current);
    return () => observer.disconnect();
  }, [trackShowMore, showMore]);

  return {
    itemsInFirstRow,
    displaySchematisationsAmount,
    isSectionExpanded,
    showMore,
    listEndRef,
  };
}
